import { MessageDescriptor } from "@lingui/core";
import { t } from "@lingui/macro";
import { message } from "antd";
import { MAX_YEAR_SEARCH, MIN_YEAR_SEARCH } from "constants/constants";
import { Translation } from "types";
import moment, { Moment } from "moment";
import { FORM_REQUEST_DISCOUNT_TYPE } from "features/webapp/form-request/type";
import { isProd, isUat } from "./environment";

export function formatNumber(
	amount: number,
	decimalCount = 0,
	decimal = ".",
	thousands = ",",
) {
	try {
		let _amount: string | number = amount;
		decimalCount = Math.abs(decimalCount);
		decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

		const negativeSign = amount < 0 ? "-" : "";

		const i: number | string = parseInt(
			(_amount = Math.abs(Number(_amount) || 0).toFixed(decimalCount)),
		).toString();
		const j = i.length > 3 ? i.length % 3 : 0;

		let decimalNum = Math.abs(Number(_amount) - Number(i))
			.toFixed(decimalCount)
			.slice(2);

		const _decimalNumArr = String(decimalNum).split("");
		let k = _decimalNumArr.length;
		while (k > 0) {
			if (_decimalNumArr[k - 1] === "0") {
				_decimalNumArr.splice(k - 1, 1);
			} else {
				k = 0;
			}
			k--;
		}
		decimalNum = _decimalNumArr.join("");

		return (
			negativeSign +
			(j ? i.substr(0, j) + thousands : "") +
			i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) +
			(decimalCount ? (decimalNum ? decimal + decimalNum : "") : "")
		);
	} catch (e) {
		console.log(e);
		return 0;
	}
}

export function formatPriceApp(price: number) {
	return formatNumber(price, 2);
}

export function isImage(url: string) {
	return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
}

export function subText(text: string, maxLength: number) {
	if (!text) return;
	let result = text;
	if (text.length > maxLength) {
		result = text.substring(0, maxLength) + "...";
	}
	return result;
}

export function getTimeElapsed(time: Date) {
	const now = moment();
	const last = moment(time);

	const seconds = now.diff(last, "seconds");
	if (seconds <= 60) return t`A few seconds`;

	const minutes = now.diff(last, "minutes");
	if (minutes <= 60) return minutes + " " + t`minutes`;

	const hours = now.diff(last, "hours");
	if (hours <= 24) return hours + " " + t`hours`;

	const days = now.diff(last, "days");
	if (days <= 7) return days + " " + t`days`;

	const weeks = now.diff(last, "weeks");
	if (weeks <= 4) return weeks + " " + t`weeks`;

	const months = now.diff(last, "months");
	if (months <= 12) return months + " " + t`months`;

	const years = now.diff(last, "years");
	if (years > 1) return years + " " + t`years`;
}

export const translate = (text: string, values?: MessageDescriptor["values"]) =>
	t({ id: text, values }) || "";

/* ignore strict mode, use any type to work around */
export const filterEmptyFields = (object: any) => {
	const result: any = {};
	Object.keys(object)
		.filter(key => object[key] !== "" && object[key] !== null)
		.forEach(key => {
			result[key] = object[key];
		});
	return result;
};

export const getLastItemInArray = (
	array: any[],
	callback: (item: any) => boolean,
	isGetIndex = true,
) => {
	for (let index = array.length - 1; index >= 0; index--) {
		if (callback(array[index])) {
			return isGetIndex ? index : array[index];
		}
	}
	return null;
};

export const enumStringToOptions = (arg: { [key: string]: string }) =>
	Object.values(arg).map((value: string) => ({
		label: translate(value.charAt(0).toUpperCase() + value.slice(1)),
		value,
	}));

export const getFileNameFromUrl = (url: string) => {
	const nameAndQuery = url.split("/")[url.split("/").length - 1];
	return nameAndQuery.split("?")[0];
};

export function beep(fileName: string) {
	const snd = new Audio(`/files/${fileName}.mp3`);
	snd.play();
}

export const customRequestUpload = ({ onSuccess }: any) => {
	setTimeout(() => {
		onSuccess("ok");
	}, 0);
};

export function delay(ms: number) {
	return new Promise(resolve => {
		setTimeout(resolve, ms);
	});
}

export const handleDownload = (fileUrl: string, name: string) => {
	const link = document.createElement("a");
	link.href = fileUrl;
	link.setAttribute("download", name);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const getListYearFilter = () => {
	const years = [];
	for (let index = MAX_YEAR_SEARCH; index >= MIN_YEAR_SEARCH; index--) {
		years.push(index);
	}
	return years;
};

export function cleanObj(obj: any) {
	const _obj = { ...obj };
	for (const propName in _obj) {
		if (
			_obj[propName] === "" ||
			obj[propName] === null ||
			obj[propName] === undefined
		) {
			delete _obj[propName];
		}
	}
	return _obj;
}

export const dateIsToday = (date: Moment) => {
	return (
		moment().startOf("day").diff(moment(date).startOf("day"), "days") === 0
	);
};

export function debounce(delay: number) {
	let timeout: number;

	return function executedFunc(func: Function) {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(func, delay);
	};
}

export function lastItem<T>(items: T[]): T {
	return items[items.length - 1];
}

export function extractHashtags(content: string = ""): string[] {
	const hashtagRegex = /#([\w\.]+)/g;
	const hashtags = content.match(hashtagRegex) || [];
	return hashtags.map(el => el.replace("#", "").toLowerCase().trim());
}

export const isOther = (text: string) => {
	return text?.toLowerCase()?.trim() == "other";
};
export const checkDisableSubmit = (translation: Translation) => {
	return Object.values(translation || {}).some(el => isOther(el));
};

export const isValidId = (id: unknown) => {
	return id && !Number.isNaN(id) && Number(id);
};

export const alertMessage = (text: string) => {
	if (text) {
		message.error(translate(text));
	}
};

export const getDiscountPercent = (
	price?: number,
	discountPercent?: number,
) => {
	if (price && discountPercent) {
		const discountValue = Math.round((price * discountPercent) / 100);
		return {
			price: discountValue,
			percent: discountPercent,
		};
	}
	return {
		price: 0,
		percent: 0,
	};
};

export const getDiscountPrice = (price?: number, discountValue?: number) => {
	if (price && discountValue) {
		let percent = Math.round((discountValue / price) * 100);
		if (percent > 99) percent = 99;
		if (percent < 1) percent = 1;
		return {
			price: discountValue,
			percent,
		};
	}
	return {
		price: 0,
		percent: 0,
	};
};
export const getDiscount = (
	price?: number,
	discountValue?: number,
	discountType?: string,
) => {
	if (!discountType || discountType === FORM_REQUEST_DISCOUNT_TYPE.PRICE) {
		return getDiscountPrice(price, discountValue);
	}
	return getDiscountPercent(price, discountValue);
};

export const capitalizeFirstLetter = (text: string) => {
	if (!text) return "";
	return text.charAt(0).toUpperCase() + text.slice(1);
};

export const roundNumber = (number: number, decimalCount = 2) => {
	return parseFloat(number.toFixed(decimalCount));
};

export const getDefaultMerchantAndStoreIds = () => {
	let merchantId = 74;
	let storeId = 220;
	if (isUat) {
		merchantId = 1;
		storeId = 1;
	} else if (isProd) {
		merchantId = 492;
		storeId = 213;
	}
	return {
		merchant: {
			id: merchantId,
			name: "Liberty Carz",
		},
		store: {
			id: storeId,
			name: "Liberty Carz",
		},
	};
};

export const getTimeExpired = (months: number) => {
	const now = moment();
	const expired = now.add(months * 30, "days");
	return moment(expired).format("YYYY-MM-DD");
};
